import Seo from "@/components/Seo";

const ORG = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://tscmservices.in/#org",
  name: "TSCM Services",
  legalName: "Law Abiding Technology",
  address: {
    "@type": "PostalAddress",
    streetAddress: "UM-A-106A, Upper Mezzanine Floor, Ansal Plaza, Vaishali",
    addressLocality: "Ghaziabad",
    postalCode: "201010",
    addressCountry: "IN",
  },
  telephone: "+91 82 59 01 01 01",
  email: "info@tscmservices.in",
  taxID: "09AACFL4204H1ZL",
};

const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://tscmservices.in/#website",
  url: "https://tscmservices.in/",
  name: "TSCM Services",
  publisher: { "@id": "https://tscmservices.in/#org" },
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    ["Will anyone in my office know you were here?", "Who is informed is your decision. Attendance is arranged to avoid identifying the visit: unmarked vehicles, no branded cases, and a time set by you."],
    ["What is the difference between a bug sweep and TSCM?", "A bug sweep is the common name for an inspection of a room, vehicle or home for hidden devices. TSCM, technical surveillance countermeasures, is the discipline that inspection belongs to: radio, electronic and physical methods, and a report of what was found."],
    ["How quickly can someone attend?", "We do not publish a fixed attendance time. The date depends on the city and the size of the space and is agreed on the call. We contact you within 12 hours at most of your enquiry."],
    ["What does it cost?", "Pricing follows the area covered and the methods required. We quote after a scoping conversation, not before it."],
    ["How often should a company do this?", "We recommend scheduled inspections through the year, and one before each board sitting or transaction discussion. What suits you depends on how often the space is left unattended and who has access to it."],
    ["Is this the same as cybersecurity?", "No. Technical surveillance countermeasures deal with the physical space and its radio environment: devices placed in a room, a vehicle or a fixture. They are not a review of your network. We can also check phones and laptops for spyware; this is scoped separately from the room inspection, and the scope is agreed on the call."],
    ["What if you find something?", "We report it to you. What you do next depends on your situation and is yours to decide with your own counsel; we do not apply one fixed procedure. The report records what was found and where."],
    ["What do I receive afterwards?", "A written report and a verbal debrief, covering the space examined, the methods used, what was found and what we recommend. It is written in plain language so that a board or a legal team can read it."],
    ["What do you need to know about the meeting?", "At minimum, the venue and the time at which we are to attend. For a date and a quote we also ask which areas are to be inspected and roughly how large they are. Why you are commissioning it is optional."],
    ["What do you keep afterwards?", "You keep one copy of the findings and notes. Our copy is held encrypted and kept for about a year, then destroyed."],
    ["Can a hotel meeting room be made secure?", "It can be inspected, and that is worth doing, but an inspection reduces risk rather than removing it: you do not control the venue, the staff, or who used the room the day before. For high-stakes discussions in a venue you do not control, an inspection followed by monitoring through the meeting is an option."],
    ["Does a clean sweep guarantee the room is secure?", "No. An inspection reports what was found within the conditions and limits recorded at the time. It confirms a space at a point in time, and what enters the room afterwards is a separate question."],
  ].map(([name, text]) => ({
    "@type": "Question",
    name,
    acceptedAnswer: { "@type": "Answer", text },
  })),
};
import { Link } from "react-router-dom";
import Header from "@/components/tscm/Header";
import Hero from "@/components/tscm/Hero";
import Situations from "@/components/tscm/Situations";
import Experience from "@/components/tscm/Experience";
import CapabilityStrip from "@/components/tscm/CapabilityStrip";
import ServiceCards from "@/components/tscm/ServiceCards";
import Credentials from "@/components/tscm/Credentials";
import Coverage from "@/components/tscm/Coverage";
import About from "@/components/tscm/About";
import PhotoBand from "@/components/tscm/PhotoBand";
import Faq from "@/components/tscm/Faq";
import Contact from "@/components/tscm/Contact";
import Footer from "@/components/tscm/Footer";
import UmbrellaBand from "@/components/tscm/UmbrellaBand";
import { Reveal } from "@/components/tscm/Reveal";

export default function Home() {
  return (
    <div id="top" className="min-h-screen bg-base-color font-body text-ink">
      <Seo
        title="TSCM Services — Counter-Surveillance Sweeps Across India"
        description="Corporate TSCM and bug sweep inspections of boardrooms, offices and meeting rooms for hidden devices, across India. By Law Abiding Technology (LAT)."
        path="/"
        jsonLd={[ORG, WEBSITE, FAQ_LD]}
      />
      <Header />
      <main>
        <Hero />
        <Situations />
        <CapabilityStrip />
        <PhotoBand
          src="/media/corporate-floor-team-sweep-wide.jpg"
          alt="The team at work across a corporate floor — checking seating, workstations and fittings"
          caption="A corporate floor — the kind of space we attend"
        />
        <UmbrellaBand id="about-us" title="About us.">
          <Experience />
        </UmbrellaBand>
        <UmbrellaBand id="services" title="Services.">
          <ServiceCards />
          <div id="programme" className="border-t border-hairline py-10 text-center">
            <Reveal>
              <p className="mx-auto max-w-xl px-5 text-base leading-relaxed text-sub">
                Inspections scheduled through the year, the room checked again
                before each board sitting —{" "}
                <Link
                  to="/preventive-tscm"
                  className="text-violet transition-colors hover:text-violeth"
                  data-testid="programme-liner-link"
                >
                  the preventive programme
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </UmbrellaBand>
        <PhotoBand
          src="/media/instrument-kit-console.jpg"
          alt="The full instrument kit laid out on a console table with its cases — spectrum analysers, a non-linear junction detector, antennas and a monitoring laptop"
          caption="The instrument kit brought to an inspection"
          framed
        />
        <Credentials />
        <About />
        <Coverage />
        <Faq />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
